import React, { useEffect, useRef } from 'react';
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router';
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@photo-booth/ui/button";
import { ArrowLeftIcon, HomeIcon, RefreshCwIcon, AlertTriangleIcon, WifiOffIcon, ServerCrashIcon, ImageOffIcon, MessageCircleIcon } from "lucide-react";
import { handleApiError, AppError } from "@/lib/errorUtils";
import { track, trackError } from "@/lib/analytics/track";
import { SOCIAL_LINE_URL } from '@/lib/constants/social';
import * as Sentry from '@sentry/react'

const ERROR_TYPES = {
  '404': {
    icon: AlertTriangleIcon,
    title: 'Page Not Found',
    message: 'The page you\'re looking for doesn\'t exist or has been moved.',
    suggestion: 'Let\'s get you back on track!'
  },
  'processing': {
    icon: AlertTriangleIcon,
    title: 'Processing Failed',
    message: 'Failed to get photo result. Please capture this page and contact us via line.',
    suggestion: 'Please capture this page and contact us via line to get your photo result or refund.'
  },
  'network': {
    icon: WifiOffIcon,
    title: 'Connection Problem',
    message: 'Unable to connect to our servers. Please check your internet connection.',
    suggestion: 'Try refreshing the page or check your connection.'
  },
  'server': {
    icon: ServerCrashIcon,
    title: 'Server Error',
    message: 'Something went wrong on our end. Our team has been notified.',
    suggestion: 'Please try again in a few moments.'
  },
  'upload': {
    icon: ImageOffIcon,
    title: 'Upload Failed',
    message: 'We couldn\'t process your photo. The file might be too large or corrupted.',
    suggestion: 'Try uploading a different photo or reduce the file size.'
  },
  'payment': {
    icon: AlertTriangleIcon,
    title: 'Payment Issue',
    message: 'There was a problem processing your payment.',
    suggestion: 'Please try again or contact support if the issue persists.'
  },
  'default': {
    icon: AlertTriangleIcon,
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    suggestion: 'Please try refreshing the page or go back to the home screen.'
  }
};

export const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const error = useRouteError();
  const trackedRef = useRef(false);
  
  // Determine error type and message from the route error
  const getErrorInfo = () => {
    // Handle React Router errors (404, etc.)
    if (isRouteErrorResponse(error)) {
      if (error.status === 404) {
        return { type: '404', message: ERROR_TYPES['404'].message };
      }
      if (error.status >= 500) {
        return { type: 'server', message: ERROR_TYPES.server.message };
      }
    }

    // Handle our custom AppError
    if (error instanceof AppError) {
      return { type: error.type, message: error.userMessage || error.message };
    }

    // Handle other errors using our utility
    if (error instanceof Error) {
      return handleApiError(error);
    }

    // Fallback
    return { type: 'default', message: ERROR_TYPES.default.message };
  };

  const { type: errorType, message: customMessage } = getErrorInfo();
  
  const errorConfig = ERROR_TYPES[errorType as keyof typeof ERROR_TYPES] || ERROR_TYPES.default;
  const IconComponent = errorConfig.icon;

  useEffect(() => {
    // Avoid double tracking in StrictMode dev.
    if (trackedRef.current) return;
    trackedRef.current = true;

    track("ui_error_page_view", {
      error_type: errorType,
      route_path: window.location?.pathname,
    });

    trackError({
      type: errorType,
      message: customMessage || errorConfig.message,
      source: "route_error",
      stack: error instanceof Error ? error.stack : undefined,
    });
    Sentry.captureException(error);
  }, [errorType, customMessage, errorConfig.message, error]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleClickContactUs = () => {
    window.open(SOCIAL_LINE_URL, '_blank');
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background flex items-center justify-center px-8" data-element="error-page">
        <div className="max-w-md w-full text-center h-full">
          {/* Error Icon */}
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 bg-vintage-red/10 rounded-full flex items-center justify-center">
              <IconComponent className="w-12 h-12 text-vintage-red" />
            </div>
          </div>

          {/* Error Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-vintage-red mb-4 font-family-edmund">
            {errorConfig.title}
          </h1>

          {/* Error Message */}
          <p className="text-vintage-black text-lg leading-relaxed mb-2">
            {customMessage || errorConfig.message}
          </p>

          {/* Suggestion */}
          <p className="text-vintage-black/70 text-base leading-relaxed mb-8">
            {errorConfig.suggestion}
          </p>

          {/* Divider */}
          <div className="text-center text-vintage-red text-2xl mb-8">⸻</div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Primary Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={handleGoHome}
                className="bg-vintage-red hover:bg-vintage-red/90 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <HomeIcon className="w-4 h-4" />
                Go Home
              </Button>
              
              {(errorType !== 'processing') && (
                <Button 
                onClick={handleRefresh}
                variant="outline"
                className="border-vintage-red text-vintage-red hover:bg-vintage-red hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <RefreshCwIcon className="w-4 h-4" />
                Try Again
              </Button>
              )}

              {errorType === 'processing' && (
                <Button 
                onClick={handleClickContactUs}
                variant="outline"
                className="border-vintage-red text-vintage-red hover:bg-vintage-red hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircleIcon className="w-4 h-4" />
                Contact Us
              </Button>
              )}
            </div>

            {/* Secondary Action */}
            <div className="pt-2">
              <Button 
                onClick={handleGoBack}
                variant="ghost"
                className="text-vintage-black/60 hover:text-vintage-black hover:bg-vintage-black/5 px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Go Back
              </Button>
            </div>
          </div>

          {/* Contact Information */}
          {(errorType === 'server' || errorType === 'payment') && (
            <div className="mt-12 pt-8 border-t border-vintage-black/10">
              <p className="text-vintage-black/60 text-sm mb-2">
                Need help? Contact us:
              </p>
              <p className="text-vintage-red font-medium">
                line.me/keptscene
              </p>
            </div>
          )}

          {/* Error Code for Development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-8 p-4 bg-vintage-black/5 rounded-lg text-left">
              <p className="text-xs text-vintage-black/60 font-mono">
                Error Type: {errorType}
              </p>
              {customMessage && (
                <p className="text-xs text-vintage-black/60 font-mono mt-1">
                  Custom Message: {customMessage}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </PageTransition>
  );
};
